import React, { useState, useEffect } from 'react';
import { PermissionService } from '../Services/PermissionService.js';
import close from '../../../assets/icons/close.svg';
import Select from 'react-select';
import UserCurrentPermissionsModal from './UserCurrentPermissionsModal.js';

const UserPermissionModal = ({ isOpen, onClose, onSave, user, moduleTree }) => {
  const [activeTab, setActiveTab] = useState('module'); // 'module' or 'independent'
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedSubmodule, setSelectedSubmodule] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [availableSubmodules, setAvailableSubmodules] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [independentPermissions, setIndependentPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCurrentPermissionsModalOpen, setIsCurrentPermissionsModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && moduleTree && user) {
      prepareModulesData();
      setUserPermissions(user.permissions || []);
    }
  }, [isOpen, moduleTree, user]);

  // Load independent permissions after user permissions are set
  useEffect(() => {
    if (isOpen && userPermissions.length >= 0) { // Check for >= 0 to handle empty arrays
      loadIndependentPermissions();
    }
  }, [isOpen, userPermissions]);

  // Reset submodule and permissions when module changes
  useEffect(() => {
    if (selectedModule && activeTab === 'module') {
      prepareSubmodulesData();
      setSelectedSubmodule(null);
      setSelectedPermissions([]);
      setAvailablePermissions([]);
    }
  }, [selectedModule, activeTab]);

  // Reset permissions when submodule changes
  useEffect(() => {
    if (selectedSubmodule && activeTab === 'module') {
      preparePermissionsData();
      setSelectedPermissions([]);
    }
  }, [selectedSubmodule, activeTab]);

  // Load permissions when submodule or tab changes with user permissions
  useEffect(() => {
    if (selectedSubmodule && activeTab === 'module' && userPermissions.length >= 0) {
      preparePermissionsData();
      setSelectedPermissions([]);
    }
  }, [selectedSubmodule, activeTab, userPermissions]);

  // Reload permissions when modal opens or tab changes
  useEffect(() => {
    if (isOpen && userPermissions.length >= 0) { // Check for >= 0 to handle empty arrays
      if (activeTab === 'independent') {
        loadIndependentPermissions();
      } else if (selectedSubmodule) {
        preparePermissionsData();
      }
      setSelectedPermissions([]);
    }
  }, [isOpen, activeTab, userPermissions]);

  const prepareModulesData = () => {
    const modules = moduleTree.map(module => ({
      value: module.id,
      label: module.name,
      module: module
    }));
    setAvailableModules(modules);
  };

  const loadIndependentPermissions = async () => {
    try {
      const response = await PermissionService.getIndependentPermissions();
      // Handle both direct array response and paginated response
      const permissionsData = Array.isArray(response) ? response : (response.results || []);
      const currentPermissionIds = getCurrentPermissionIds();
      
      const allPermissions = permissionsData.map(permission => ({
        value: permission.id,
        label: permission.name,
        permission: permission
      }));

      // Always filter out already assigned permissions (only for assign mode)
      const filteredPermissions = allPermissions.filter(perm => !currentPermissionIds.includes(perm.value));

      setIndependentPermissions(filteredPermissions);
    } catch (error) {
      console.error('Error loading independent permissions:', error);
    }
  };

  const prepareSubmodulesData = () => {
    if (!selectedModule) return;
    
    const submodules = selectedModule.module.submodules.map(submodule => ({
      value: submodule.id,
      label: submodule.name,
      submodule: submodule
    }));
    setAvailableSubmodules(submodules);
  };

  const preparePermissionsData = () => {
    if (!selectedSubmodule) return;
    
    const currentPermissionIds = getCurrentPermissionIds();
    const allPermissions = selectedSubmodule.submodule.permissions.map(permission => ({
      value: permission.id,
      label: permission.name,
      permission: permission
    }));

    // Always filter out already assigned permissions (only for assign mode)
    const filteredPermissions = allPermissions.filter(perm => !currentPermissionIds.includes(perm.value));

    setAvailablePermissions(filteredPermissions);
  };

  const handleModuleChange = (selectedOption) => {
    setSelectedModule(selectedOption);
  };

  const handleSubmoduleChange = (selectedOption) => {
    setSelectedSubmodule(selectedOption);
  };

  const handlePermissionChange = (selectedOptions) => {
    setSelectedPermissions(selectedOptions || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedPermissions.length === 0) return;

    setLoading(true);
    try {
      const permissionIds = selectedPermissions.map(perm => perm.value);
      const submitData = {
        user_ids: [user.id],
        permission_ids: permissionIds,
        action: 'assign'
      };

      await onSave(submitData);
      handleReset();
    } catch (error) {
      console.error('Error saving user permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveTab('module');
    setSelectedModule(null);
    setSelectedSubmodule(null);
    setSelectedPermissions([]);
    setAvailableSubmodules([]);
    setAvailablePermissions([]);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setSelectedPermissions([]);
    if (tab === 'module') {
      setSelectedModule(null);
      setSelectedSubmodule(null);
      setAvailableSubmodules([]);
      setAvailablePermissions([]);
    }
  };

  const getCurrentPermissionIds = () => {
    return userPermissions?.map(perm => perm.id) || [];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '600px', maxWidth: '90%' }}>
        <div className="modal-header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 className="text-primary" style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#222F3E' }}>
            Manage Permissions for {user?.full_name}
          </h2>
          <button className="close-btn" onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <img src={close} alt="close" />
          </button>
        </div>

        {/* User Info */}
        <div style={{ 
          background: '#F9F9F9', 
          padding: '12px 16px', 
          borderRadius: '6px', 
          marginBottom: '20px',
          border: '1px solid #E9E9E9'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong style={{ fontSize: '14px', color: '#222F3E' }}>{user?.full_name}</strong>
              <div style={{ fontSize: '12px', color: '#6B778C', marginTop: '2px' }}>
                Mobile: {user?.mobile} | Status: {user?.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6B778C' }}>Current Permissions</div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#7F56DA' }}>
                  {userPermissions?.length || 0}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsCurrentPermissionsModalOpen(true)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#7F56DA',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                View Details
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tab Navigation */}
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #f0f0f0', 
            marginBottom: '20px',
            gap: '1px'
          }}>
            <button
              type="button"
              onClick={() => handleTabSwitch('module')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: activeTab === 'module' ? '#7F56DA' : '#f8f9fa',
                color: activeTab === 'module' ? 'white' : '#6B778C',
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Module Permissions
            </button>
            <button
              type="button"
              onClick={() => handleTabSwitch('independent')}
              style={{
                padding: '12px 24px',
                border: 'none',
                backgroundColor: activeTab === 'independent' ? '#7F56DA' : '#f8f9fa',
                color: activeTab === 'independent' ? 'white' : '#6B778C',
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              Independent Permissions
            </button>
          </div>

          {/* Module Permissions Tab */}
          {activeTab === 'module' && (
            <div className="module-permissions-tab">
              {/* Module Selection */}
              <div className="row">
                <div className="col-md-12">
                  <label className="form-labell">Select Module</label>
                  <Select
                    options={availableModules}
                    value={selectedModule}
                    onChange={handleModuleChange}
                    placeholder="Choose a module..."
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px'
                      }),
                      valueContainer: (base) => ({ 
                        ...base, 
                        padding: '2px 6px', 
                        fontSize: '14px' 
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '14px',
                        backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                        color: state.isSelected ? 'white' : '#222F3E',
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Submodule Selection */}
              <div className="row" style={{ marginTop: '16px' }}>
                <div className="col-md-12">
                  <label className="form-labell">Select Submodule</label>
                  <Select
                    options={availableSubmodules}
                    value={selectedSubmodule}
                    onChange={handleSubmoduleChange}
                    placeholder="Choose a submodule..."
                    isClearable
                    isDisabled={!selectedModule}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base, state) => ({ 
                        ...base, 
                        minHeight: '45px',
                        backgroundColor: state.isDisabled ? '#f5f5f5' : base.backgroundColor,
                        borderColor: state.isDisabled ? '#ddd' : base.borderColor
                      }),
                      valueContainer: (base) => ({ 
                        ...base, 
                        padding: '2px 6px', 
                        fontSize: '14px' 
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '14px',
                        backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                        color: state.isSelected ? 'white' : '#222F3E',
                      }),
                    }}
                  />
                </div>
              </div>

              {/* Permissions Selection */}
              <div className="row" style={{ marginTop: '16px' }}>
                <div className="col-md-12">
                  <label className="form-labell">Select Permissions</label>
                  <Select
                    options={availablePermissions}
                    value={selectedPermissions}
                    onChange={handlePermissionChange}
                    placeholder="Choose permissions..."
                    isMulti
                    isClearable
                    isDisabled={!selectedSubmodule}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base, state) => ({ 
                        ...base, 
                        minHeight: '45px',
                        backgroundColor: state.isDisabled ? '#f5f5f5' : base.backgroundColor,
                        borderColor: state.isDisabled ? '#ddd' : base.borderColor
                      }),
                      valueContainer: (base) => ({ 
                        ...base, 
                        padding: '2px 6px', 
                        fontSize: '14px' 
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '14px',
                        backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                        color: state.isSelected ? 'white' : '#222F3E',
                      }),
                    }}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6B778C', 
                    marginTop: '6px',
                    fontStyle: 'italic'
                  }}>
                    Only showing permissions not already assigned to this user
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Independent Permissions Tab */}
          {activeTab === 'independent' && (
            <div className="independent-permissions-tab">
              <div className="row">
                <div className="col-md-12">
                  <label className="form-labell">Select Independent Permissions</label>
                  <Select
                    options={independentPermissions}
                    value={selectedPermissions}
                    onChange={handlePermissionChange}
                    placeholder="Choose independent permissions..."
                    isMulti
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '45px'
                      }),
                      valueContainer: (base) => ({ 
                        ...base, 
                        padding: '2px 6px', 
                        fontSize: '14px' 
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: '14px',
                        backgroundColor: state.isSelected ? '#7F56DA' : state.isFocused ? '#F2EDFF' : base.backgroundColor,
                        color: state.isSelected ? 'white' : '#222F3E',
                      }),
                    }}
                  />
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6B778C', 
                    marginTop: '6px',
                    fontStyle: 'italic'
                  }}>
                    <div>These permissions are not associated with any specific module or submodule</div>
                    <div style={{ color: '#7F56DA', marginTop: '2px' }}>
                      Only showing permissions not already assigned to this user
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Info Section */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#E8F5E8',
            border: '1px solid #A7E7A7',
            borderRadius: '6px',
            fontSize: '14px',
            color: '#2D5016'
          }}>
            ℹ️ Only showing permissions not already assigned to this user. To remove existing permissions, use the "View Details" button above.
          </div>

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                backgroundColor: '#ccc',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                border: 'none',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedPermissions.length === 0}
              style={{
                backgroundColor: loading || selectedPermissions.length === 0 ? '#ccc' : '#7F56DA',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: loading || selectedPermissions.length === 0 ? 'not-allowed' : 'pointer',
                border: 'none',
                fontSize: '14px'
              }}
            >
              {loading ? 'Assigning...' : 'Assign Permissions'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Current Permissions Modal */}
      <UserCurrentPermissionsModal
        isOpen={isCurrentPermissionsModalOpen}
        onClose={() => setIsCurrentPermissionsModalOpen(false)}
        user={user}
        onRevokePermissions={onSave}
      />
    </div>
  );
};

export default UserPermissionModal;